"use client"

import { useEffect, useState, useCallback, useRef } from "react"
import FingerprintJS from "@fingerprintjs/fingerprintjs"
import { useDemoStore } from "@/store/demoStore"

export interface UseDemoLimitReturn {
  visitorId: string | null
  remaining: number | null
  isLoaded: boolean
  consumeQuery: () => Promise<boolean>
}

const CONSENT_KEY = "demo-consent-given"
const VISITOR_ID_KEY = "demo-visitor-id"

export const useDemoLimit = (): UseDemoLimitReturn => {
  const [visitorId, setVisitorId] = useState<string | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const isInitializingRef = useRef(false)
  
  // Read remaining and dailyLimit from store
  const remaining = useDemoStore((state) => state.remaining)
  const dailyLimit = useDemoStore((state) => state.dailyLimit)
  
  // Track consent state to trigger re-renders when it changes
  const [hasConsentState, setHasConsentState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false
    return localStorage.getItem(CONSENT_KEY) === "true"
  })

  // Check if consent was given
  const hasConsent = useCallback(() => {
    if (typeof window === "undefined") return false
    return localStorage.getItem(CONSENT_KEY) === "true"
  }, [])
  
  // Listen for consent changes
  useEffect(() => {
    const checkConsent = () => {
      const consent = hasConsent()
      setHasConsentState(consent)
    }
    
    // Check initial consent
    checkConsent()
    
    // Listen for storage events (from other tabs/windows)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CONSENT_KEY) {
        checkConsent()
      }
    }
    window.addEventListener("storage", handleStorageChange)
    
    // Poll for consent changes (for same-tab changes)
    const interval = setInterval(() => {
      checkConsent()
    }, 500) // Check every 500ms
    
    return () => {
      window.removeEventListener("storage", handleStorageChange)
      clearInterval(interval)
    }
  }, [hasConsent])

  // Save visitorId to localStorage
  const saveVisitorId = useCallback((id: string) => {
    if (typeof window === "undefined") return
    localStorage.setItem(VISITOR_ID_KEY, id)
  }, [])

  // Initialize fingerprinting and usage tracking
  const initializeFingerprint = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isInitializingRef.current) {
      return
    }
    
    isInitializingRef.current = true
    let id: string

    // Check if we already have a stored visitorId
    const storedId = typeof window !== "undefined" ? localStorage.getItem(VISITOR_ID_KEY) : null
    const isFallbackFormat = storedId ? /^\d+-\d+\.\d+$/.test(storedId) : false
    
    // If we have consent and stored ID is a fallback, re-initialize with FingerprintJS
    if (storedId && !isFallbackFormat) {
      // Use stored FingerprintJS ID (not a fallback)
      id = storedId
    } else if (!hasConsent()) {
      // No consent - generate numeric session ID
      id = `${Date.now()}-${Math.random()}`
      saveVisitorId(id)
    } else {
      // Has consent - either no stored ID or stored ID is a fallback, so use FingerprintJS
      // Try to get fingerprint
      try {
        const fp = await FingerprintJS.load()
        const result = await fp.get()
        id = result.visitorId
        saveVisitorId(id)
      } catch (error) {
        // Only log in development to avoid console errors in Lighthouse
        if (process.env.NODE_ENV === 'development') {
          console.error("FingerprintJS failed:", error)
        }
        // Fallback to numeric session ID
        id = `${Date.now()}-${Math.random()}`
        saveVisitorId(id)
      }
    }

    setVisitorId(id)

    // Remaining is now managed by the store based on server data
    // We just mark as loaded
    setIsLoaded(true)
    isInitializingRef.current = false
  }, [hasConsent, saveVisitorId])

  // Consume a query
  const consumeQuery = useCallback(async (): Promise<boolean> => {
    if (!visitorId) {
      return false
    }

    // Check store's remaining (set to 0 when DAILY_LIMIT exception is received)
    const store = useDemoStore.getState()
    
    // If limit is explicitly reached, don't allow
    if (store.limitReached) {
      return false
    }
    
    // If remaining is null, allow submission (server will validate)
    // If remaining is set and <= 0, don't allow
    if (store.remaining !== null && store.remaining <= 0) {
      return false
    }

    // Allow if remaining is null (server will handle) or if remaining > 0
    return true
  }, [visitorId])

  useEffect(() => {
    if (hasConsentState) {
      // When consent is given, initialize FingerprintJS
      // Don't clear visitorId - keep the current one (fallback) until FingerprintJS completes
      if (!isInitializingRef.current) {
        // Preserve current visitorId if it exists (fallback format)
        // This ensures the socket doesn't see null during the async operation
        if (!visitorId) {
          // If visitorId is null, get it from localStorage (might be a fallback)
          const storedId = typeof window !== "undefined" ? localStorage.getItem(VISITOR_ID_KEY) : null
          if (storedId) {
            setVisitorId(storedId)
          }
        }
        initializeFingerprint()
      }
    } else {
      // Wait for consent - still initialize with session fallback
      const storedId = typeof window !== "undefined" ? localStorage.getItem(VISITOR_ID_KEY) : null
      const sessionId = storedId || `${Date.now()}-${Math.random()}`
      
      if (!storedId) {
        saveVisitorId(sessionId)
      }
      
      setVisitorId(sessionId)
      // Remaining is now managed by the store based on server data
      setIsLoaded(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConsentState, initializeFingerprint, saveVisitorId])

  return {
    visitorId,
    remaining,
    isLoaded,
    consumeQuery,
  }
}
